import React, { useState } from 'react';
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useUpdateBiConnectionNetwork } from 'api/bi';
import { extractApiError } from 'api/errors';
import { createSqlAllowedIpsInput, parseSqlAllowedIps, SqlAllowedIpsField } from './SqlNetworkAccess';

export default function NetworkAccessDialog({ connection, onClose, onReload, clientIp = null }) {
  const { t } = useTranslation();
  const updateNetwork = useUpdateBiConnectionNetwork();
  const [value, setValue] = useState(() => createSqlAllowedIpsInput(connection.sql_allowed_ips));
  const [revision, setRevision] = useState(connection.network_revision);
  const [conflict, setConflict] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [error, setError] = useState(null);
  const [fieldError, setFieldError] = useState(null);
  const pending = updateNetwork.isPending || reloading;

  const save = async () => {
    setError(null);
    setFieldError(null);
    try {
      await updateNetwork.mutateAsync({ id: connection.id, sql_allowed_ips: parseSqlAllowedIps(value), expected_revision: revision });
      onClose();
    } catch (err) {
      if (err.response?.status === 409) setConflict(true);
      else if (err.code === 'invalid' || err.code === 'limit') setFieldError(t(err.code === 'limit' ? 'data.sqlAllowedIpsLimit' : 'data.sqlAllowedIpsInvalid'));
      else if (err.response?.data?.sql_allowed_ips) setFieldError(t('data.sqlAllowedIpsInvalid'));
      else setError(extractApiError(err, t('data.sqlNetworkSaveFailed')));
    }
  };

  const reload = async () => {
    setReloading(true);
    setError(null);
    try {
      const latest = await onReload(connection.id);
      if (!latest || latest.status === 'revoked') {
        setError(t('data.sqlNetworkConnectionUnavailable'));
        return;
      }
      setValue(createSqlAllowedIpsInput(latest.sql_allowed_ips));
      setRevision(latest.network_revision);
      setConflict(false);
      setFieldError(null);
    } catch (err) {
      setError(extractApiError(err, t('data.sqlNetworkReloadFailed')));
    } finally {
      setReloading(false);
    }
  };

  return (
    <Dialog open onClose={pending ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('data.editSqlNetworkAccess')}</DialogTitle>
      <DialogContent>
        <Typography variant="body2">{connection.name}</Typography>
        {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
        {conflict && <Alert severity="warning" sx={{ mb: 1 }}>{t('data.sqlNetworkConflict')}</Alert>}
        <SqlAllowedIpsField
          clientIp={clientIp}
          value={value}
          onChange={(next) => { setValue(next); setFieldError(null); }}
          disabled={pending || conflict}
          error={fieldError}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={pending}>{t('data.cancel')}</Button>
        {conflict ? (
          <Button onClick={reload} disabled={pending} variant="contained">{t('data.sqlNetworkReload')}</Button>
        ) : (
          <Button onClick={save} disabled={pending} variant="contained">{t('data.save')}</Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
